import React from "react"
import DashboardHeader from "../../components/DashboardHeader"
import DeviceStatsCards from "../../components/DeviceStatsCards"
import LatestFirmwareCard from "../../components/LatestFirmwareCard"
import LiveDataControls from "../../components/LiveDataControls"
import ManualControls from "../../components/ManualControls"
import ViewModeSelector from "../../components/ViewModeSelector"
import ErrorDisplay from "../../components/ErrorDisplay"
import DeviceTable from "../../components/DeviceTable"
import {JobProgressPanel} from "../../components/JobProgressPanel"
import DeviceGrid from "../../components/DeviceGrid"
import LoadingBanner from "../../components/LoadingBanner"
import {useDeviceDashboard} from "../../hooks/useDeviceDashboard"

export const DeviceDashboard: React.FC = () => {
	const {
		shadowError,
		connectivityError,
		isLiveMode,
		liveUpdateInterval,
		customInterval,
		viewMode,
		setViewMode,
		selectedFile,
		selectedRtlFile,
		selectedDevices,
		cardsPerRow,
		setCardsPerRow,
		showFavoritesOnly,
		setShowFavoritesOnly,
		isLoading,
		connectivityLoading,
		firmwareUploadLoading,
		firmwareUploadError,
		firmwareUploadSuccess,
		rtlUploadLoading,
		rtlUploadError,
		rtlUploadSuccess,
		latestFirmware,
		jobProgress,
		currentJobId,
		macAddresses,
		deviceStats,
		filteredDeviceArray,
		tableData,
		userDevices,
		stopJobProgressPolling,
		clearFirmwareUploadStatus,
		clearRtlUploadStatus,
		getDisplayConnectionStatus,
		handleRefreshDevice,
		handleRefreshDevices,
		handleRefreshConnectivity,
		handleToggleLiveMode,
		handleIntervalChange,
		handleClearError,
		handleFileSelect,
		handleRtlFileSelect,
		handleFileUpload,
		handleRtlFileUpload,
		handleRtlCiUpload,
		preUploadClearing,
		singleSelectedDeviceType,
		handleCancelQueued,
		handleCancelInProgress,
		handleCancelDeviceJobs,
		handleClearAllAlarms,
		handleClearAll,
		handleDeviceSelectionChange,
	} = useDeviceDashboard()

	return (
		<div className="max-w-7xl mx-auto pt-6 pb-10">
			<div className="mb-8">
				<DashboardHeader isLiveMode={isLiveMode} />

				<DeviceStatsCards stats={deviceStats} />

				{latestFirmware && <LatestFirmwareCard firmware={latestFirmware} />}

				{currentJobId && jobProgress && (
					<JobProgressPanel jobProgress={jobProgress} onStopMonitoring={stopJobProgressPolling} />
				)}

				<LiveDataControls
					isLiveMode={isLiveMode}
					liveUpdateInterval={liveUpdateInterval}
					customInterval={customInterval}
					macAddresses={macAddresses}
					onToggleLiveMode={handleToggleLiveMode}
					onIntervalChange={handleIntervalChange}
				/>
				<ManualControls
					isLoading={isLoading}
					connectivityLoading={connectivityLoading}
					firmwareUploadLoading={firmwareUploadLoading}
					firmwareUploadSuccess={firmwareUploadSuccess}
					firmwareUploadError={firmwareUploadError}
					selectedFile={selectedFile}
					selectedDevices={selectedDevices}
					rtlUploadLoading={rtlUploadLoading}
					rtlUploadSuccess={rtlUploadSuccess}
					rtlUploadError={rtlUploadError}
					selectedRtlFile={selectedRtlFile}
					onRefreshDevices={handleRefreshDevices}
					onRefreshConnectivity={handleRefreshConnectivity}
					onClearAll={handleClearAll}
					onFileSelect={handleFileSelect}
					onFileUpload={handleFileUpload}
					onClearFirmwareStatus={clearFirmwareUploadStatus}
					onRtlFileSelect={handleRtlFileSelect}
					onRtlFileUpload={handleRtlFileUpload}
					onRtlCiUpload={handleRtlCiUpload}
					preUploadClearing={preUploadClearing}
					singleSelectedDeviceType={singleSelectedDeviceType}
					onClearRtlStatus={clearRtlUploadStatus}
					onCancelQueued={handleCancelQueued}
					onCancelInProgress={handleCancelInProgress}
					onCancelDeviceJobs={handleCancelDeviceJobs}
					onClearAllAlarms={handleClearAllAlarms}
				/>
				<ViewModeSelector
					viewMode={viewMode}
					onViewModeChange={setViewMode}
					cardsPerRow={cardsPerRow}
					onCardsPerRowChange={setCardsPerRow}
					showFavoritesOnly={showFavoritesOnly}
					onShowFavoritesOnlyChange={setShowFavoritesOnly}
				/>
				<ErrorDisplay
					shadowError={shadowError}
					connectivityError={connectivityError}
					onClearError={handleClearError}
				/>
				{isLoading && !isLiveMode && <LoadingBanner connectivityLoading={connectivityLoading} />}
			</div>
			{viewMode === "table" ? (
				<DeviceTable
					data={tableData}
					onRefresh={handleRefreshDevice}
					selectedDevices={selectedDevices}
					onDeviceSelectionChange={handleDeviceSelectionChange}
				/>
			) : (
				<DeviceGrid
					devices={filteredDeviceArray}
					cardsPerRow={cardsPerRow as 1 | 2 | 3 | 4}
					isLiveMode={isLiveMode}
					showFavoritesOnly={showFavoritesOnly}
					hasUserDevices={userDevices.length > 0}
					getConnectionStatus={getDisplayConnectionStatus}
					onRefresh={handleRefreshDevice}
				/>
			)}
		</div>
	)
}

export default DeviceDashboard
